import {
  useSections,
  useUpdateCatalogueRaisonneSections,
} from "@/api/cms";
import {
  createEmptyDecoupageUpsert,
  DecoupageSectionInlineEditor,
  DECOUPAGE_SORT_OPTIONS,
  DECOUPAGE_TYPES_ARTWORKS,
  DECOUPAGE_TYPES_MULTIPRINTS,
} from "@/components/cms-puck/fields/DecoupageSectionInlineEditor";
import { LinkTargetField } from "@/components/cms-puck/fields/LinkTargetField";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAuth } from "@/context/auth/AuthContext";
import { getMainLanguage } from "@/i18n";
import type {
  CatalogueRaisonneSection,
  CatalogueRaisonneSectionUpsert,
} from "@/lib/cms/cms-types";
import {
  createCatalogueDecoupageListItem,
  defaultDecoupageItemLink,
  normalizeCatalogueDecoupageListItems,
  type CatalogueDecoupageListItem,
  type CatalogueDecoupagesTarget,
} from "@/lib/cms-puck/blocks/catalogue-decoupages";
import { type ButtonLink } from "@/lib/cms-puck/blocks/button";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { toast } from "@/hooks/use-toast";
import { usePuck } from "@measured/puck";
import type { ComponentData } from "@measured/puck";
import { useQueryClient } from "@tanstack/react-query";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  Trash2Icon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/** Libellés type découpage — mêmes clés que l'ancien CMS (`DecoupageCatalogueRaisonneForm`). */
function decoupageTypeLabel(
  type: string,
  target: CatalogueDecoupagesTarget,
  t: (key: string) => string,
): string {
  switch (type) {
    case "creationDate":
      return t("creationDate");
    case "categories":
      return t("oeuvresDescriptionCategorie");
    case "series":
      return t("oeuvresDescriptionSerie");
    case "themes":
      return t("oeuvresDescriptionTheme");
    case "registrationNumber":
      return t("registrationNumber");
    case "type":
      return target === "MULTIPRINTS"
        ? t("estampesMultiplesDescriptionType")
        : t("typeOeuvre");
    default:
      return type;
  }
}

/** Libellés tri interne — mêmes clés que l'ancien CMS. */
function decoupageSortLabel(sort: string, t: (key: string) => string): string {
  const map: Record<string, string> = {
    "dateCreation:asc": "sortByDateCreationAsc",
    "dateCreation:desc": "sortByDateCreationDesc",
    "title:asc": "sortByTitleAsc",
    "title:desc": "sortByTitleDesc",
    "numeroEnregistrement:asc": "sortByNumeroAsc",
    "numeroEnregistrement:desc": "sortByNumeroDesc",
    "createdAt:asc": "sortByCreatedAtAsc",
    "createdAt:desc": "sortByCreatedAtDesc",
  };
  const key = map[sort];
  return key ? t(key) : sort;
}

type BlockProps = {
  id?: string;
  target?: CatalogueDecoupagesTarget;
  columns?: 2 | 3 | 4;
  showSectionName?: boolean;
  nameAlign?: "left" | "center" | "right";
  items?: CatalogueDecoupageListItem[];
};

type DrawerState =
  | { mode: "create"; draft: CatalogueRaisonneSectionUpsert }
  | { mode: "edit"; itemId: string }
  | null;

function sectionLabel(
  section: CatalogueRaisonneSection | undefined,
  fallback: string,
): string {
  if (!section) return fallback;
  const main = getMainLanguage();
  const name =
    section.nameMultilingual?.[main as keyof typeof section.nameMultilingual] ??
    section.nameMultilingual?.fr ??
    "";
  return name.trim() || fallback;
}

function defaultTypeForTarget(target: CatalogueDecoupagesTarget): string {
  return target === "MULTIPRINTS"
    ? DECOUPAGE_TYPES_MULTIPRINTS[0]
    : DECOUPAGE_TYPES_ARTWORKS[0];
}

/**
 * Éditeur sidebar du bloc CatalogueDecoupages — liste curatée façon Sélection,
 * CRUD sections CR global via drawer, lien PAGE|CUSTOM par carte.
 *
 * Type de découpage = réglage commun (comme l'ancien CMS) :
 * changer le type filtre la liste sur les sections enregistrées de ce type
 * et adapte les champs du formulaire (catégorie / plage…).
 */
export function CatalogueDecoupagesField() {
  const cmsNew = useCmsNew();
  const { t } = useTranslation();
  const { selectedItem, dispatch, getSelectorForId } = usePuck();
  const { instanceData } = useAuth();
  const cmsId = instanceData?.CMS?.id ?? "";
  const queryClient = useQueryClient();
  const props = (selectedItem?.props ?? {}) as BlockProps;
  const blockId = props.id;
  const [drawer, setDrawer] = useState<DrawerState>(null);

  const target: CatalogueDecoupagesTarget =
    props.target === "MULTIPRINTS" ? "MULTIPRINTS" : "ARTWORKS";
  const items = normalizeCatalogueDecoupageListItems(props.items, target);
  const columns = props.columns ?? 4;
  const showSectionName = props.showSectionName !== false;
  const nameAlign = props.nameAlign ?? "center";

  const { data: sections = [], isLoading } = useSections(cmsId, target);
  const { mutate: mutateSections, isPending } =
    useUpdateCatalogueRaisonneSections();

  const typeOptions = useMemo(
    () =>
      (target === "MULTIPRINTS"
        ? DECOUPAGE_TYPES_MULTIPRINTS
        : DECOUPAGE_TYPES_ARTWORKS
      ).map((value) => ({
        value,
        label: decoupageTypeLabel(value, target, t),
      })),
    [target, t],
  );

  const sortOptions = useMemo(
    () =>
      DECOUPAGE_SORT_OPTIONS.map((value) => ({
        value,
        label: decoupageSortLabel(value, t),
      })),
    [t],
  );

  /** Type / tri locaux — ne pas réécrire le CR au simple changement de type. */
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedSort, setSelectedSort] = useState<string | null>(null);
  const typeInitRef = useRef<string | null>(null);
  const seededKeyRef = useRef<string | null>(null);
  const prevTargetRef = useRef(target);

  // Reset local type/sort when Œuvres ↔ Multiples change.
  useEffect(() => {
    if (prevTargetRef.current === target) return;
    prevTargetRef.current = target;
    typeInitRef.current = null;
    seededKeyRef.current = null;
    setSelectedType(null);
    setSelectedSort(null);
  }, [target]);

  useEffect(() => {
    if (isLoading) return;
    const key = `${cmsId}:${target}`;
    if (typeInitRef.current === key) return;
    typeInitRef.current = key;
    const first = sections[0];
    setSelectedType(first?.type || defaultTypeForTarget(target));
    setSelectedSort(first?.sortByInsideDecoupage ?? "dateCreation:asc");
  }, [cmsId, target, sections, isLoading]);

  // Ne pas retomber sur creationDate tant que les sections ne sont pas
  // initialisées : sinon le select affiche « Année début/fin » alors que
  // les données enregistrées sont p.ex. en `themes` (filtre catégorie).
  const sharedType =
    selectedType ??
    sections[0]?.type ??
    defaultTypeForTarget(target);
  const sharedSort =
    selectedSort ??
    sections[0]?.sortByInsideDecoupage ??
    "dateCreation:asc";

  /** Sections du type sélectionné — parité `filter(s => s.type === selectedType)`. */
  const sectionsOfType = useMemo(
    () => sections.filter((s) => s.type === sharedType),
    [sections, sharedType],
  );

  const sectionById = useMemo(() => {
    const map = new Map<string, CatalogueRaisonneSection>();
    for (const s of sections) map.set(s.id, s);
    return map;
  }, [sections]);

  const unusedSections = useMemo(
    () =>
      sectionsOfType.filter((s) => !items.some((it) => it.sectionId === s.id)),
    [sectionsOfType, items],
  );

  const canEdit =
    !!selectedItem &&
    selectedItem.type === "CatalogueDecoupages" &&
    !!blockId;

  const patch = (partial: Partial<BlockProps>) => {
    if (!blockId || !selectedItem) return;
    const selector = getSelectorForId(blockId);
    if (!selector) return;
    const node = selectedItem as ComponentData;
    dispatch({
      type: "replace",
      destinationIndex: selector.index,
      destinationZone: selector.zone,
      data: {
        ...node,
        props: { ...node.props, ...partial },
      },
    });
  };

  useEffect(() => {
    if (!canEdit || isLoading || selectedType == null) return;
    const key = `${blockId}:${target}:${sharedType}`;
    if (seededKeyRef.current === key) return;
    seededKeyRef.current = key;

    const validIds = new Set(sectionsOfType.map((s) => s.id));
    const kept = items.filter((it) => validIds.has(it.sectionId));

    if (items.length === 0 && sectionsOfType.length > 0) {
      patch({
        items: sectionsOfType.map((s) =>
          createCatalogueDecoupageListItem(s.id, { target }),
        ),
      });
      return;
    }

    // Items d'un autre type (ou orphelins) → recentrer sur le type sélectionné.
    if (kept.length !== items.length) {
      if (kept.length > 0) {
        patch({ items: kept });
      } else if (sectionsOfType.length > 0) {
        patch({
          items: sectionsOfType.map((s) =>
            createCatalogueDecoupageListItem(s.id, { target }),
          ),
        });
      } else {
        patch({ items: [] });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync 1× par type
  }, [
    canEdit,
    blockId,
    target,
    sharedType,
    selectedType,
    sectionsOfType,
    isLoading,
  ]);

  if (!canEdit) {
    return (
      <p className="p-1 text-xs text-slate-500">
        {cmsNew(
          "cms.new.hint.catalogueDecoupages.select",
          "Sélectionnez le bloc Découpages.",
        )}
      </p>
    );
  }

  const sectionToUpsert = (
    s: CatalogueRaisonneSection,
    orderFallback: number,
  ): CatalogueRaisonneSectionUpsert => ({
    id: s.id,
    nameMultilingual: s.nameMultilingual,
    titleMultilingual: s.titleMultilingual,
    contentMultilingual: s.contentMultilingual,
    showContent: s.showContent,
    startValue: s.startValue ?? "",
    endValue: s.endValue ?? "",
    categoryId: s.categoryId ?? "",
    categoryMultilingual: s.categoryMultilingual,
    vignette: s.vignette,
    order: s.order ?? orderFallback,
    type: s.type,
    sortByInsideDecoupage: s.sortByInsideDecoupage,
  });

  /**
   * Parité ancien CMS : un enregistrement remplace toute la cible par les
   * découpages du type courant (deleteMany + createMany). Changer de type
   * sans sauver ne fait que filtrer l'UI.
   */
  const persistSections = (
    nextOfType: CatalogueRaisonneSectionUpsert[],
    opts?: {
      type?: string;
      sortBy?: string;
      onDone?: (saved: CatalogueRaisonneSectionUpsert[]) => void;
    },
  ) => {
    if (!cmsId) {
      toast({
        title: cmsNew("cms.new.auto.title.erreur", "Erreur"),
        description: "CMS id manquant",
        variant: "error",
      });
      return;
    }
    const type = opts?.type ?? sharedType ?? "creationDate";
    const sortBy = opts?.sortBy ?? sharedSort ?? "dateCreation:asc";

    const current = nextOfType.map((d, i) => ({
      ...d,
      type,
      sortByInsideDecoupage: sortBy,
      order: i + 1,
    }));

    mutateSections(
      {
        cmsId,
        type,
        target,
        decoupages: current,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["sections", cmsId, target],
          });
          toast({
            title: cmsNew("cms.new.auto.title.enregistre", "Enregistré"),
            description: cmsNew(
              "cms.new.auto.text.decoupages_cr_enregistres",
              "Découpages du catalogue raisonné mis à jour.",
            ),
            variant: "success",
          });
          opts?.onDone?.(nextOfType);
        },
        onError: () => {
          toast({
            title: cmsNew("cms.new.auto.title.erreur", "Erreur"),
            description: cmsNew(
              "cms.new.auto.text.echec_decoupages",
              "Impossible d'enregistrer les découpages.",
            ),
            variant: "error",
          });
        },
      },
    );
  };

  const sectionsOfTypeAsUpsert = (): CatalogueRaisonneSectionUpsert[] =>
    sectionsOfType.map((s, i) => sectionToUpsert(s, i + 1));

  const onTypeChange = (nextType: string) => {
    setSelectedType(nextType);
    setDrawer(null);
    // Toujours partir de `sections` (tous types) — pas de la liste filtrée.
    const filtered = sections.filter((s) => s.type === nextType);
    setSelectedSort(
      filtered[0]?.sortByInsideDecoupage ?? "dateCreation:asc",
    );
    seededKeyRef.current = `${blockId}:${target}:${nextType}`;
    patch({
      items: filtered.map((s) =>
        createCatalogueDecoupageListItem(s.id, { target }),
      ),
    });
  };

  const onSortChange = (nextSort: string) => {
    setSelectedSort(nextSort);
    if (!sectionsOfType.length) return;
    persistSections(sectionsOfTypeAsUpsert(), {
      type: sharedType,
      sortBy: nextSort,
    });
  };

  const updateItem = (
    id: string,
    partial: Partial<CatalogueDecoupageListItem>,
  ) => {
    patch({
      items: items.map((it) => (it.id === id ? { ...it, ...partial } : it)),
    });
  };

  const moveItem = (index: number, dir: -1 | 1) => {
    const next = [...items];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    patch({ items: next });
  };

  const openCreateDrawer = () => {
    setDrawer({
      mode: "create",
      draft: createEmptyDecoupageUpsert(
        sectionsOfType.length + 1,
        sharedType,
        sharedSort,
      ),
    });
  };

  const addExisting = (sectionId: string) => {
    if (items.some((it) => it.sectionId === sectionId)) return;
    const item = createCatalogueDecoupageListItem(sectionId, { target });
    patch({ items: [...items, item] });
    setDrawer({ mode: "edit", itemId: item.id });
  };

  const saveCreated = (upsert: CatalogueRaisonneSectionUpsert) => {
    if (!drawer || drawer.mode !== "create") return;
    const id = String(upsert.id ?? drawer.draft.id);
    const next = [...sectionsOfTypeAsUpsert(), { ...upsert, id }];
    persistSections(next, {
      onDone: () => {
        const item = createCatalogueDecoupageListItem(id, { target });
        patch({ items: [...items, item] });
        setDrawer({ mode: "edit", itemId: item.id });
      },
    });
  };

  const saveSection = (
    sectionId: string,
    upsert: CatalogueRaisonneSectionUpsert,
  ) => {
    const base = sectionsOfTypeAsUpsert();
    const idx = base.findIndex((s) => s.id === sectionId);
    const next =
      idx >= 0
        ? base.map((s) =>
            s.id === sectionId ? { ...upsert, id: sectionId } : s,
          )
        : [...base, { ...upsert, id: sectionId }];
    persistSections(next);
  };

  const deleteFromCatalogue = (sectionId: string) => {
    if (
      !window.confirm(
        cmsNew(
          "cms.new.confirm.delete_decoupage_cr",
          "Supprimer ce découpage du catalogue raisonné ? Il disparaîtra aussi des autres pages.",
        ),
      )
    ) {
      return;
    }
    const next = sectionsOfTypeAsUpsert().filter((s) => s.id !== sectionId);
    persistSections(next, {
      onDone: () => {
        patch({ items: items.filter((it) => it.sectionId !== sectionId) });
        setDrawer(null);
      },
    });
  };

  const editItem =
    drawer?.mode === "edit"
      ? items.find((it) => it.id === drawer.itemId)
      : undefined;
  const editSection = editItem
    ? sectionById.get(editItem.sectionId)
    : undefined;

  return (
    <div className="flex flex-col gap-3 p-1">
      <p className="text-xs text-slate-500">
        {cmsNew(
          "cms.new.hint.catalogueDecoupages.manage",
          "Curatez les cartes de ce bloc et leur lien. Créer / modifier un découpage met à jour le catalogue raisonné (global).",
        )}
      </p>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-slate-600">
          {cmsNew("cms.new.auto.label.cible", "Cible")}
        </Label>
        <div className="flex gap-2">
          {(
            [
              ["ARTWORKS", "Œuvres"],
              ["MULTIPRINTS", "Multiples"],
            ] as const
          ).map(([t, label]) => (
            <Button
              key={t}
              type="button"
              size="sm"
              variant={target === t ? "default" : "outline"}
              onClick={() => {
                patch({ target: t, items: [] });
              }}
            >
              {cmsNew(
                t === "ARTWORKS"
                  ? "cms.new.auto.label.uvres"
                  : "cms.new.auto.label.multiples",
                label,
              )}
            </Button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-slate-600">
          {cmsNew("cms.new.auto.label.type_decoupage", "Type de découpage")}
        </Label>
        <Select
          value={sharedType}
          onValueChange={onTypeChange}
          disabled={isPending}
        >
          <SelectTrigger className="h-8 bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {typeOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-slate-400">
          {cmsNew(
            "cms.new.hint.catalogueDecoupages.typeFiltersList",
            "Affiche les découpages de ce type. Un enregistrement remplace les découpages des autres types (comme l'ancien CMS).",
          )}
        </p>
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-slate-600">
          {cmsNew("cms.new.auto.label.tri_interne", "Tri dans le listing")}
        </Label>
        <Select
          value={sharedSort}
          onValueChange={onSortChange}
          disabled={isPending || sectionsOfType.length === 0}
        >
          <SelectTrigger className="h-8 bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {sortOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-slate-600">
          {cmsNew("cms.new.auto.label.colonnes", "Colonnes")}
        </Label>
        <Select
          value={String(columns)}
          onValueChange={(v) =>
            patch({ columns: Number(v) as 2 | 3 | 4 })
          }
        >
          <SelectTrigger className="h-8 bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[2, 3, 4].map((n) => (
              <SelectItem key={n} value={String(n)}>
                {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-slate-600">
          {cmsNew(
            "cms.new.auto.label.afficher_le_nom_de_la_section",
            "Afficher le nom",
          )}
        </Label>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant={showSectionName ? "default" : "outline"}
            onClick={() => patch({ showSectionName: true })}
          >
            {cmsNew("cms.new.auto.label.oui", "Oui")}
          </Button>
          <Button
            type="button"
            size="sm"
            variant={!showSectionName ? "default" : "outline"}
            onClick={() => patch({ showSectionName: false })}
          >
            {cmsNew("cms.new.auto.label.non", "Non")}
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-slate-600">
          {cmsNew("cms.new.auto.label.alignement_du_nom", "Alignement du nom")}
        </Label>
        <Select
          value={nameAlign}
          onValueChange={(v) =>
            patch({ nameAlign: v as "left" | "center" | "right" })
          }
        >
          <SelectTrigger className="h-8 bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="left">
              {cmsNew("cms.new.auto.label.gauche", "Gauche")}
            </SelectItem>
            <SelectItem value="center">
              {cmsNew("cms.new.auto.label.centre", "Centré")}
            </SelectItem>
            <SelectItem value="right">
              {cmsNew("cms.new.auto.label.droite", "Droite")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-slate-700">
          {cmsNew("cms.new.auto.label.decoupages", "Découpages")} ({items.length}
          )
        </span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={isPending || !cmsId}
          onClick={openCreateDrawer}
        >
          <PlusIcon className="mr-1 size-3.5" />
          {cmsNew(
            "cms.new.auto.action.add_decoupage",
            "Ajouter un découpage",
          )}
        </Button>
      </div>

      {items.length === 0 && !isLoading ? (
        <p className="text-xs text-slate-500">
          {sectionsOfType.length === 0
            ? cmsNew(
                "cms.new.hint.catalogueDecoupages.noCrSectionsForType",
                "Aucun découpage enregistré pour ce type. Créez-en avec « Ajouter un découpage ».",
              )
            : cmsNew(
                "cms.new.hint.catalogueDecoupages.emptyItems",
                "Liste vide — les découpages du catalogue seront ajoutés automatiquement.",
              )}
        </p>
      ) : null}

      <ul className="flex flex-col gap-1.5">
        {items.map((item, index) => {
          const section = sectionById.get(item.sectionId);
          return (
            <li
              key={item.id}
              className="flex items-center gap-1 rounded border border-slate-200 bg-slate-50/60 px-2 py-1.5"
            >
              <button
                type="button"
                className="min-w-0 flex-1 truncate text-left text-sm font-medium text-slate-800 hover:underline"
                onClick={() => setDrawer({ mode: "edit", itemId: item.id })}
              >
                {sectionLabel(
                  section,
                  cmsNew(
                    "cms.new.auto.text.decoupage_introuvable",
                    "Découpage introuvable",
                  ),
                )}
              </button>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="size-7"
                disabled={index === 0}
                onClick={() => moveItem(index, -1)}
              >
                <ChevronUpIcon className="size-3.5" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="size-7"
                disabled={index === items.length - 1}
                onClick={() => moveItem(index, 1)}
              >
                <ChevronDownIcon className="size-3.5" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="size-7 text-red-600"
                title={cmsNew(
                  "cms.new.auto.action.remove_from_list",
                  "Retirer de la liste",
                )}
                onClick={() =>
                  patch({
                    items: items.filter((it) => it.id !== item.id),
                  })
                }
              >
                <Trash2Icon className="size-3.5" />
              </Button>
            </li>
          );
        })}
      </ul>

      <Sheet
        // modal={false} : le MediaPicker est porté sur `document.body` hors du
        // Sheet — en modal Radix, body a pointer-events:none et bloque le picker.
        modal={false}
        open={drawer != null}
        onOpenChange={(open) => {
          if (!open) setDrawer(null);
        }}
      >
        <SheetContent
          side="right"
          className="z-[100] flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-xl"
          // Ne pas fermer sur clic / focus hors sheet (MediaPicker, Select…) :
          // sinon on perd le brouillon de découpage en cours.
          onInteractOutside={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onFocusOutside={(e) => e.preventDefault()}
        >
          <SheetHeader className="shrink-0 border-b px-6 py-4 pr-12 text-left">
            <SheetTitle>
              {drawer?.mode === "create"
                ? cmsNew(
                    "cms.new.auto.title.nouveau_decoupage",
                    "Nouveau découpage",
                  )
                : sectionLabel(
                    editSection,
                    cmsNew(
                      "cms.new.auto.label.decoupage",
                      "Découpage",
                    ),
                  )}
            </SheetTitle>
            <SheetDescription>
              {drawer?.mode === "create"
                ? cmsNew(
                    "cms.new.hint.catalogueDecoupages.createDrawer",
                    "Créez un découpage dans le catalogue raisonné, ou ajoutez-en un déjà existant à cette liste.",
                  )
                : cmsNew(
                    "cms.new.hint.catalogueDecoupages.editDrawer",
                    "Lien de la carte et édition du découpage (catalogue global).",
                  )}
            </SheetDescription>
          </SheetHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
            {drawer?.mode === "create" ? (
              <div className="flex flex-col gap-4">
                {unusedSections.length > 0 ? (
                  <div className="flex flex-col gap-1.5 rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <Label className="text-xs font-medium text-slate-700">
                      {cmsNew(
                        "cms.new.auto.action.add_existing",
                        "Ajouter un existant",
                      )}
                    </Label>
                    <Select onValueChange={(id) => addExisting(id)}>
                      <SelectTrigger className="h-9 bg-white">
                        <SelectValue
                          placeholder={cmsNew(
                            "cms.new.seo.path.chooseDecoupage",
                            "Choisir…",
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent className="z-[110]">
                        {unusedSections.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {sectionLabel(s, s.id)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}

                <DecoupageSectionInlineEditor
                  key={`${drawer.draft.id}:${sharedType}`}
                  target={target}
                  section={drawer.draft}
                  decoupageType={sharedType}
                  sortByInsideDecoupage={sharedSort}
                  saving={isPending}
                  onSave={saveCreated}
                />
              </div>
            ) : null}

            {drawer?.mode === "edit" && editItem ? (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label className="text-xs font-medium text-slate-700">
                    {cmsNew("cms.new.auto.label.lien_carte", "Lien de la carte")}
                  </Label>
                  <LinkTargetField
                    value={
                      editItem.link ??
                      defaultDecoupageItemLink(editItem.sectionId, target)
                    }
                    onChange={(link: ButtonLink) =>
                      updateItem(editItem.id, { link })
                    }
                    allowedTargets={["CATALOG_DECOUPAGE", "PAGE", "CUSTOM"]}
                    fixedCatalogDecoupage={{
                      target,
                      decoupageId: editItem.sectionId,
                    }}
                  />
                </div>

                {editSection ? (
                  <>
                    <DecoupageSectionInlineEditor
                      key={`${editSection.id}:${sharedType}:${sharedSort}`}
                      target={target}
                      section={editSection}
                      decoupageType={sharedType}
                      sortByInsideDecoupage={sharedSort}
                      saving={isPending}
                      onSave={(upsert) =>
                        saveSection(editSection.id, upsert)
                      }
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="border-red-200 text-red-700"
                      disabled={isPending}
                      onClick={() => deleteFromCatalogue(editSection.id)}
                    >
                      {cmsNew(
                        "cms.new.auto.action.delete_from_cr",
                        "Supprimer du catalogue raisonné",
                      )}
                    </Button>
                  </>
                ) : (
                  <p className="text-xs text-amber-700">
                    {cmsNew(
                      "cms.new.hint.catalogueDecoupages.missingSection",
                      "Section absente du catalogue — retirez cette carte ou recréez le découpage.",
                    )}
                  </p>
                )}
              </div>
            ) : null}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
